import { Fragment } from 'react'
import { MenuItemUnified } from './menu-item-unified'

import {
  getMenuHelpCenter,
  getMenuHome,
  getMenuLogout,
  getMenuMyCompany,
  getMenuMyProfile,
  getMenuMyTeam
} from './menu-section-list-utils'
import { AccountWithCustomProperties } from '@/presentation/contexts/auth'
import { useLDClient } from 'launchdarkly-react-client-sdk'
import { MenuRenderer } from './menu-renderer'
import { AccountRole } from '@/data/usecases/account/account-role'
import { EnrollmentCategory } from '@/domain/models/content/enrollment-category'
import { FeatureFlagName, getFlagValue } from '@/infra/feature-flag'
import { MenuItemCollapsible } from './menu-item-collapsible'
import { Divider, ListItem, SvgIconTypeMap, Typography } from '@mui/material'
import PathConstants from '@/main/routes/paths'
import { OverridableComponent } from '@mui/material/OverridableComponent'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import StarBorderOutlinedIcon from '@mui/icons-material/StarBorderOutlined'
import { i18nTranslationHelper } from '@/i18n'
import ProfileAssessmentButton from '../../../button/profile-assessment-button'
type MenuSectionWrapperProps = {
  account: AccountWithCustomProperties
  variant: 'desktop' | 'mobile'
  handleClose: () => void
}

export function MenuList({ variant = 'desktop', account, handleClose }: MenuSectionWrapperProps) {
  const t = i18nTranslationHelper.getTComponents

  const ldClient = useLDClient()
  const isCompanyManager = AccountRole.contains(AccountRole.Enum.COMPANY_MANAGER, account.roles)
  const isTeamManager = AccountRole.contains(AccountRole.Enum.TEAM_MANAGER, account.roles)

  // Get menu configurations
  const menuHome = getMenuHome()
  const menuHelpCenter = getMenuHelpCenter(account)
  const menuMyProfile = getMenuMyProfile(account, ldClient)
  const menuMyTeam = isTeamManager ? getMenuMyTeam() : null
  const menuMyCompany = isCompanyManager ? getMenuMyCompany(account, ldClient) : null
  const menuLogout = getMenuLogout()

  // Navigation categories (only for mobile)
  const restrictions = account.company.contentRestrictions.categories
  const enrollmentCategoryList = EnrollmentCategory.getAvailables(restrictions)
  const isProfileAssessmentEnabled = getFlagValue(
    FeatureFlagName.COMPANIES_WITH_PROFILE_ASSESSMENT,
    ldClient
  )

  return (
    <Fragment>
      {isCompanyManager && (
        <Fragment>
          <Divider component="li" sx={{ my: 0.5 }} />
          <MenuItemUnified
            variant={variant}
            route={menuHome.route}
            icon={menuHome.icon}
            onClose={handleClose}
          />
        </Fragment>
      )}

      {menuHelpCenter && (
        <MenuRenderer variant={variant} section={menuHelpCenter} handleClose={handleClose} />
      )}

      <MenuRenderer variant={variant} section={menuMyProfile} handleClose={handleClose} />

      {menuMyTeam && (
        <MenuRenderer variant={variant} section={menuMyTeam} handleClose={handleClose} />
      )}

      {variant === 'mobile' ? (
        <Fragment>
          <Divider component="li" sx={{ my: 0.5 }} />
          <ListItem>
            <Typography variant="caption" color="common.black">
              {t('navMenu.mobile.listHeader')}
            </Typography>
          </ListItem>

          {enrollmentCategoryList.map(item =>
            item.hasMultipleCategories ? (
              <MenuItemCollapsible
                key={item.name}
                variant="mobile"
                icon={
                  (item.icon || HomeOutlinedIcon) as OverridableComponent<
                    SvgIconTypeMap<object, 'svg'>
                  > & { muiName: string }
                }
                title={item.title}
              >
                {item.categories.map(c => (
                  <MenuItemUnified
                    key={c.name}
                    variant="mobile"
                    route={PathConstants.findRouteByCategory(c)}
                    icon={
                      (c.icon || HomeOutlinedIcon) as OverridableComponent<
                        SvgIconTypeMap<object, 'svg'>
                      > & { muiName: string }
                    }
                    onClose={handleClose}
                  />
                ))}
              </MenuItemCollapsible>
            ) : (
              <MenuItemUnified
                key={item.name}
                variant="mobile"
                route={{
                  value: PathConstants.getCategoryRoutePath(item.categories[0]),
                  title: item.title,
                  name: item.name
                }}
                icon={
                  (item.icon || HomeOutlinedIcon) as OverridableComponent<
                    SvgIconTypeMap<object, 'svg'>
                  > & { muiName: string }
                }
                onClose={handleClose}
              />
            )
          )}

          {/* Institutions */}
          <MenuItemUnified
            variant="mobile"
            route={PathConstants.Routes.INSTITUTIONS}
            icon={StarBorderOutlinedIcon}
            onClose={handleClose}
          />
        </Fragment>
      ) : null}

      {/* Profile Assessment Button (conditional) */}
      {isProfileAssessmentEnabled && <ProfileAssessmentButton mode="mobile" />}

      {menuMyCompany && (
        <MenuRenderer variant={variant} section={menuMyCompany} handleClose={handleClose} />
      )}

      <MenuRenderer variant={variant} section={menuLogout} handleClose={handleClose} />
    </Fragment>
  )
}
